import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';

import {
  AddFormComponent,
  FormField,
} from '@shared/components/add-form/add-form.component';
import { PaymentControllerService } from '../../../../../../api/payment-service/api/paymentController.service';
import { PaymentSearchResultDTO } from '../../../../../../api/payment-service/model/models';
import {
  formatCurrency,
  getPaymentStatusDisplayText,
  getPaymentMethodDisplayText,
  getPaymentPlatformDisplayText,
  getPaymentStatusSeverity,
  getPaymentStatusIcon,
  getPaymentStatusStyle,
} from '../../payment-history.config';
import { DateFormatterService } from '@shared/services/date-formatter.service';

interface PaymentFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: any[];
  listRoute: string;
}

@Component({
  selector: 'app-view-history',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    TagModule,
    AddFormComponent,
  ],
  templateUrl: './view-history.component.html',
  styleUrl: './view-history.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewHistoryComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly paymentControllerService = inject(PaymentControllerService);
  private readonly dateFormatterService = inject(DateFormatterService);

  private readonly destroy$ = new Subject<void>();

  // Signals
  paymentDetails = signal<PaymentSearchResultDTO | null>(null);
  loading = signal(false);

  // Computed signal for formatted payment data
  formattedPaymentData = computed(() => {
    const payment = this.paymentDetails();
    if (!payment) return {};

    return {
      orderCode: payment.orderCode || '',
      amount: formatCurrency(payment.amount || 0, payment.currency || 'VND'),
      currency: payment.currency || '',
      payType: this.translateService.instant(
        getPaymentMethodDisplayText(payment.payType || '')
      ),
      platform: this.translateService.instant(
        getPaymentPlatformDisplayText(payment.platform || '')
      ),
      status: this.translateService.instant(
        getPaymentStatusDisplayText(payment.status || '')
      ),
      providerReference: payment.providerReference || '',
      description: payment.description || '',
      creationDate: this.dateFormatterService.formatDateTime(
        payment.creationDate || ''
      ),
      lastUpdateDate: this.dateFormatterService.formatDateTime(
        payment.lastUpdateDate || ''
      ),
    };
  });

  // Form configuration
  formConfig: PaymentFormConfig = {
    title: this.translateService.instant('payment_history.view.title'),
    formFields: this.createPaymentFormFields(),
    headerActions: [
      {
        label: 'common.actions.cancel',
        action: 'cancel',
        outlined: true,
        severity: 'secondary',
      },
    ],
    listRoute: '/finance/payment-history',
  };

  ngOnInit(): void {
    this.loadPaymentDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createPaymentFormFields(): FormField[] {
    return [
      {
        controlName: 'orderCode',
        label: 'payment_history.table.columns.order_code',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'amount',
        label: 'payment_history.table.columns.amount',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'currency',
        label: 'payment_history.table.columns.currency',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'payType',
        label: 'payment_history.table.columns.pay_type',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'platform',
        label: 'payment_history.table.columns.platform',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'status',
        label: 'payment_history.table.columns.status',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'providerReference',
        label: 'payment_history.table.columns.provider_reference',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'description',
        label: 'payment_history.table.columns.description',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'creationDate',
        label: 'payment_history.table.columns.creation_date',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
      {
        controlName: 'lastUpdateDate',
        label: 'payment_history.table.columns.last_update_date',
        type: 'text',
        section: 'payment_history.view.sections.basic_information',
        disabled: true,
      },
    ];
  }

  private loadPaymentDetails(): void {
    const paymentId = this.route.snapshot.paramMap.get('id');
    if (!paymentId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'payment_history.messages.error.failed_to_load'
        ),
      });
      this.router.navigate(['/finance/payment-history']);
      return;
    }

    this.loading.set(true);
    this.paymentControllerService
      .getPayment(+paymentId)
      .pipe(
        switchMap((response: any): Observable<PaymentSearchResultDTO> => {
          // Handle both blob and JSON responses
          if (response instanceof Blob) {
            // Convert blob to text and parse as JSON
            return new Observable<PaymentSearchResultDTO>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const jsonData = JSON.parse(reader.result as string);
                  observer.next(jsonData);
                  observer.complete();
                } catch (error) {
                  console.error('Error parsing blob response:', error);
                  observer.error(error);
                }
              };
              reader.onerror = () => {
                console.error('Error reading blob');
                observer.error(new Error('Failed to read blob'));
              };
              reader.readAsText(response);
            });
          } else {
            // If response is already JSON
            return new Observable<PaymentSearchResultDTO>((observer) => {
              observer.next(response);
              observer.complete();
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (payment: PaymentSearchResultDTO) => {
          this.paymentDetails.set(payment);
          this.loading.set(false);
        },
        error: (error: any) => {
          console.error('Error loading payment details:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'payment_history.messages.error.failed_to_load'
            ),
          });
          this.loading.set(false);
          this.router.navigate(['/finance/payment-history']);
        },
      });
  }

  // Handle form actions (like cancel button)
  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.router.navigate([this.formConfig.listRoute]);
    }
  }

  // Helper methods for template
  formatCurrency = formatCurrency;
  formatDate = (dateString: string) =>
    this.dateFormatterService.formatDateTime(dateString);
  getPaymentMethodDisplayText = getPaymentMethodDisplayText;
  getPaymentPlatformDisplayText = getPaymentPlatformDisplayText;
  getPaymentStatusDisplayText = getPaymentStatusDisplayText;
  getPaymentStatusSeverity = getPaymentStatusSeverity;
  getPaymentStatusIcon = getPaymentStatusIcon;
  getPaymentStatusStyle = getPaymentStatusStyle;
}
